import axios from 'axios';
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '@shared/types';
import {
  mockUser,
  mockTodayReport,
  mockHistoryReports,
  mockLinkedPatients,
  mockFamilies,
  mockContactLogs,
  mockAttendanceCalendar,
  mockHealthDataExtended,
  getMockPhotoUrl,
  mockLinkTokenVerification,
  mockMessagesThread,
  mockMessagesThreads,
  isDevelopmentMode,
  getMockScenario
} from './mockData';
import type { ContactLogDetail } from '../types/contactLog';
import liffManager from './liff';

// 開発モード用モックトークン（UUID v4形式）
const MOCK_INVITATION_TOKEN = '12345678-1234-4123-8123-123456789abc';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'https://api.family-app.example.com';
console.log('[API Client] Base URL:', API_BASE_URL);
console.log('[API Client] Environment:', (import.meta as any).env);

// デバッグ用にグローバルに公開
(window as any).__API_BASE_URL = API_BASE_URL;
(window as any).__API_ENV = (import.meta as any).env;

export interface ReportData {
  resident_id: string;
  date: string;
  highlights: string[];
  vitals: {
    temperature?: number;
    blood_pressure_sys?: number;
    blood_pressure_dia?: number;
    pulse?: number;
    spo2?: number;
  };
  activities: string[];
  meals: {
    breakfast?: boolean;
    lunch?: boolean;
    snack?: boolean;
  };
  hydration_ml?: number;
  bath: boolean;
  toilet_count?: number;
  medications: boolean;
  comment?: string;
  status: 'draft' | 'published';
  alert_level?: 'normal' | 'attention' | 'urgent';
  created_at: string;
  updated_at: string;
}

export interface FamilyUser {
  line_user_id: string;
  display_name: string;
  picture_url?: string;
  resident_ids: string[];
  notification_settings: {
    enabled: boolean;
    time_start: string;
    time_end: string;
    frequency: 'daily' | 'important_only';
  };
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface ChatResponse {
  response: string;
  sources?: string[];
  escalated: boolean;
  conversation_id: string;
}

// Family message types
export type MessageDirection = 'outgoing' | 'incoming';

export interface ReplyTo {
  type: 'contact_comment';
  source_date: string;
  source_content: string;
  facility_name?: string;
}

export interface FamilyMessage {
  message_id: number;
  direction: MessageDirection;
  content: string;
  sender_name?: string;
  created_at: string;
  confirmed_at?: string;
  reply_to?: ReplyTo;
}

export interface MessageThreadDetail {
  thread_id: string;
  hide_from_patient: boolean;
  messages: FamilyMessage[];
}

export interface MessageThread {
  patient_id: number;
  office_id: string;
  office_name: string;
  office_phone: string;
  threads: MessageThreadDetail[];
  has_more: boolean;
}

export interface SendMessageRequest {
  patient_id: number;
  content: string;
  reply_to?: ReplyTo;
  thread_id?: string;
  hide_from_patient?: boolean;
}

export interface SendMessageResponse {
  success: boolean;
  message_id: number;
  patient_id: number;
  thread_id?: string;
  created_at: string;
  /** 保存した送信者名（`users.display_name`）。未登録なら空文字 */
  sender_name?: string;
}

// Message confirmed status types (lightweight polling)
export interface MessageConfirmedStatusItem {
  thread_id: string;
  message_id: number;
  confirmed_at: string | null;
}

export interface MessageConfirmedStatusResponse {
  statuses: MessageConfirmedStatusItem[];
}

export type VitalType = 'temperature' | 'blood_pressure_pulse' | 'spo2' | 'hydration';

export interface HealthDataPoint {
  date: string;
  attendance_id?: string;
  vitals?: {
    temp_c?: number;
    sbp?: number;
    dbp?: number;
    pulse?: number;
    spo2?: number;
  };
  meals?: {
    staple_intake: number; // 主食摂取割合（0=なし, 1~9=n割, 10=完食）
    side_intake: number; // 副食摂取割合（0=なし, 1~9=n割, 10=完食）
    soup_intake: number; // 汁食摂取割合（0=なし, 1~9=n割, 10=完食）
    is_not_provided: boolean; // 食事提供なし
  };
  hydration_ml?: number;
  // Legacy fields for backward compatibility
  temperature?: number;
  blood_pressure_sys?: number;
  blood_pressure_dia?: number;
  pulse?: number;
  spo2?: number;
  meals_count?: number;
  medication_taken?: boolean;
  main_meal_intake?: number;
  side_meal_intake?: number;
}

export interface VitalsSummary {
  avg: number;
  max: number;
  min: number;
}

export interface HydrationSummary {
  total: number;
  avg: number;
  max: number;
}

export interface MealIntakeSummary {
  avg: number;
}

export interface MealsSummary {
  staple_intake?: MealIntakeSummary;
  side_intake?: MealIntakeSummary;
  soup_intake?: MealIntakeSummary;
}

export interface HealthDataResponse {
  patient_id: number;
  period: {
    start_date: string;
    end_date: string;
  };
  health_data: HealthDataPoint[];
  summary: {
    days_with_data: number;
    temperature?: VitalsSummary;
    blood_pressure_sys?: VitalsSummary;
    blood_pressure_dia?: VitalsSummary;
    pulse?: VitalsSummary;
    spo2?: VitalsSummary;
    hydration?: HydrationSummary;
    meals?: MealsSummary;
  };
}

export interface FamilyMember {
  line_user_id: string;
  display_name?: string;
  patient_id: number;
  patient_name?: string;
  patient_display_name?: string;  // Custom display name set by this family member
  relationship: string;
  role: string;
  created_at: string;
  updated_at: string;
}

export interface FamilyDetail extends FamilyMember {
  patient_name: string;
  office_id?: string;
}

// AI Chat types
export interface AiChatMessage {
  message_id: string;
  role: 'user' | 'assistant';
  content: string;
  quick_replies?: string[];
  created_at: string;
}

export interface AiChatConversation {
  conversation_id: string;
  patient_id?: number;
  topic: 'daily' | 'health' | 'memory';
  messages: AiChatMessage[];
}

export interface AiChatCreateResponse {
  conversation_id: string;
  topic: 'daily' | 'health' | 'memory';
  message: AiChatMessage;
}

export interface AiChatMessageResponse {
  conversation_id: string;
  message: AiChatMessage;
}

export interface AiChatSummaryResponse {
  conversation_id: string;
  summary: string;
  mood: 'positive' | 'neutral' | 'concerned';
  topics: string[];
}

export interface SaijikiEntry {
  date_key: string;
  title: string;
  description: string;
  detail: string;
  season: string;
  category: string;
  conversation_starters: string[];
  is_fallback?: boolean;
}

/**
 * 開発モード用: `?mock_scenario=` に応じた招待リンクエラーを返す。
 * 該当シナリオでなければ null。バックエンド services/api/handlers/link.py の
 * 400 レスポンス（`error_code` 付き）と同じ形を再現する。
 * verifyLinkToken / confirmFamilyLink で共通利用（重複排除）。
 */
const buildMockLinkScenarioError = (): ApiError | null => {
  switch (getMockScenario()) {
    case 'already_used':
      return {
        error: 'Token has already been used',
        message: 'Token has already been used',
        statusCode: 400,
        details: { error: 'Token has already been used', error_code: 'TOKEN_ALREADY_USED' },
      };
    case 'expired':
      return {
        error: 'Token has expired',
        message: 'Token has expired',
        statusCode: 400,
        details: { error: 'Token has expired', error_code: 'TOKEN_EXPIRED' },
      };
    default:
      return null;
  }
};

class ApiClient {
  private client: AxiosInstance;
  private idToken: string | null = null;
  private isRefreshingToken = false;

  constructor() {
    // Initialize token from localStorage if available
    this.idToken = localStorage.getItem('lineIdToken');

    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
      console.log(`[API Request] Data:`, config.data);

      // Always try to get the latest token (memory or localStorage)
      const token = this.idToken || localStorage.getItem('lineIdToken');
      console.log(`[API Request] Token available:`, token ? 'Yes' : 'No');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log(`[API Request] Authorization header set`);
      } else {
        console.warn(`[API Request] No token available for ${config.url}`);
      }

      return config;
    });

    this.client.interceptors.response.use(
      (response) => {
        console.log(`[API Response] ${response.status} ${response.config.url}`);
        console.log(`[API Response] Data:`, response.data);
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // 401エラーでトークンリフレッシュを1回だけ試行
        if (error.response?.status === 401 && !originalRequest._retry) {
          // リトライフラグを設定（無限ループ防止）
          originalRequest._retry = true;

          // 既にトークンリフレッシュ中の場合は待機
          if (this.isRefreshingToken) {
            console.log('[API Client] Token refresh already in progress, waiting...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            const newToken = this.getIdToken();
            if (newToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client.request(originalRequest);
            }
          }

          try {
            console.log('[API Client] 401 error detected, attempting token refresh...');
            this.isRefreshingToken = true;

            // LIFF SDKから新しいID Tokenを取得
            await liffManager.init();

            if (!liffManager.isLoggedIn()) {
              console.error('[API Client] LIFF session expired, redirecting to login...');
              this.clearIdToken();
              window.location.href = '/link_token';
              throw error;
            }

            const newToken = await liffManager.getIdToken();
            console.log('[API Client] New token acquired, retrying request...');

            // 新しいトークンを保存
            this.setIdToken(newToken);

            // 元のリクエストを新しいトークンでリトライ
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }

            return this.client.request(originalRequest);
          } catch (refreshError) {
            console.error('[API Client] Token refresh failed:', refreshError);
            this.clearIdToken();
            // ログインページへリダイレクト
            window.location.href = '/link_token';
            throw refreshError;
          } finally {
            this.isRefreshingToken = false;
          }
        }

        const apiError: ApiError = {
          error: error.code || 'UNKNOWN_ERROR',
          message: error.message || 'An unknown error occurred',
          statusCode: error.response?.status || 500,
        };

        if (error.response?.data) {
          const responseData = error.response.data as Record<string, unknown>;
          apiError.error = (responseData.error as string) || apiError.error;
          // バックエンドの `error_response` (services/api/middleware/response.py) は
          // 日本語メッセージを `error` フィールドに格納し、`message` を返さない。
          // `message` が無ければ `error` をフォールバックに使い、Axios 既定の英語
          // メッセージ ("Request failed with status code ...") が露出しないようにする。
          apiError.message =
            (responseData.message as string) ||
            (responseData.error as string) ||
            apiError.message;
          // `error_response` が `error_details` を body 直下にマージしているため
          // (例: { error, reason, service_end_date }) 任意フィールドを `details` で保持する
          apiError.details = responseData;
        }

        console.error(`[API Error] ${error.config?.url}:`, apiError);
        throw apiError;
      }
    );
  }

  setIdToken(token: string): void {
    this.idToken = token;
    // Save to localStorage for components that need direct access
    localStorage.setItem('lineIdToken', token);
  }

  clearIdToken(): void {
    this.idToken = null;
    localStorage.removeItem('lineIdToken');
  }

  getIdToken(): string | null {
    return this.idToken || localStorage.getItem('lineIdToken');
  }

  async verifyLineAuth(idToken: string): Promise<FamilyUser> {
    // 開発モードの場合はモックユーザーを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 500)); // 遅延をシミュレート
      return mockUser;
    }

    const response = await this.client.post('/auth/line/verify', {
      id_token: idToken,
    });
    return response.data;
  }

  async getReports(residentId: string, date?: string): Promise<ReportData[]> {
    const params: any = { resident_id: residentId };
    if (date) {
      params.date = date;
    }

    const response = await this.client.get('/reports', { params });
    return response.data;
  }

  async getReport(residentId: string, date: string): Promise<ReportData> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 300));
      const today = new Date().toISOString().split('T')[0];
      if (date === today) {
        return mockTodayReport;
      }
      // 他の日付の場合は履歴データから探す
      const report = mockHistoryReports.find(r => r.date === date);
      if (!report) {
        const error: ApiError = {
          error: 'NOT_FOUND',
          message: 'Report not found for the specified date',
          statusCode: 404,
        };
        throw error;
      }
      return report;
    }

    const response = await this.client.get('/reports', {
      params: { resident_id: residentId, date }
    });
    
    if (!response.data || response.data.length === 0) {
      const error: ApiError = {
        error: 'NOT_FOUND',
        message: 'Report not found for the specified date',
        statusCode: 404,
      };
      throw error;
    }
    
    return response.data[0];
  }

  async getReportHistory(residentId: string, limit = 30): Promise<ReportData[]> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 400));
      return mockHistoryReports.slice(0, limit);
    }

    const response = await this.client.get('/reports/history', {
      params: { resident_id: residentId, limit }
    });
    return response.data;
  }

  async updateNotificationSettings(settings: FamilyUser['notification_settings']): Promise<void> {
    // 開発モードの場合は成功をシミュレート
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 600));
      console.log('Mock: Settings updated', settings);
      return;
    }

    await this.client.put('/family/notification-settings', settings);
  }

  async chatQuery(message: string, conversationId?: string): Promise<ChatResponse> {
    const response = await this.client.post('/chat/query', {
      message,
      conversation_id: conversationId,
    });
    return response.data;
  }

  /**
   * 招待トークンを検証して患者情報を取得
   */
  async verifyLinkToken(token: string): Promise<{
    patient_id: number;
    patient_name: string;
    office_id: string;
    office_name: string;
    company_name: string;
    /**
     * 旧 `consents` テーブル由来の第三者提供同意フラグ（後方互換）。
     * BE は `has_third_party_consent` に置き換わっており、フェーズ 3 で削除予定。
     * 新規実装では `has_third_party_consent` を参照する。
     */
    has_current_consent: boolean;
    /**
     * 新モデル `third_party_consents` 由来の第三者提供同意フラグ。
     * `false` なら家族 LIFF は「まだご利用いただけません」画面 (`NotYetAvailable`) を返す。
     */
    has_third_party_consent: boolean;
  }> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 300));
      // ?mock_scenario=already_used / expired : 招待リンクエラーを再現
      const scenarioError = buildMockLinkScenarioError();
      if (scenarioError) {
        throw scenarioError;
      }
      // トークンが特定の値でない場合はエラーを返す
      if (token !== MOCK_INVITATION_TOKEN) {
        const error: ApiError = {
          error: 'INVALID_TOKEN',
          message: 'Invalid or expired invitation token',
          statusCode: 400,
        };
        throw error;
      }
      // ?mock_scenario=no_third_party_consent: 事業所側で第三者提供同意が未取得な
      // 招待リンクを再現。家族 LIFF 側の NotYetAvailable 画面の動作確認に使う
      // （PR-3 レビュー指摘 / mockLinkTokenVerification のコメントに合わせて実装追加）。
      if (getMockScenario() === 'no_third_party_consent') {
        return { ...mockLinkTokenVerification, has_third_party_consent: false };
      }
      return mockLinkTokenVerification;
    }

    const response = await this.client.post('/api/link/verify', { token });
    return response.data;
  }

  /**
   * 家族リンクを確認・確立
   */
  async confirmFamilyLink(data: {
    token: string;
    line_user_id: string;
    relationship: string;
  }): Promise<{
    message: string;
    patient_id: number;
    patient?: {
      patient_id: number;
      patient_name: string;
      role: string;
      office_id: string;
      office_name: string;
    };
  }> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 500));
      // ?mock_scenario=already_used / expired : 招待リンクエラーを再現
      const scenarioError = buildMockLinkScenarioError();
      if (scenarioError) {
        throw scenarioError;
      }
      if (data.token !== MOCK_INVITATION_TOKEN) {
        const error: ApiError = {
          error: 'INVALID_TOKEN',
          message: 'Invalid or expired invitation token',
          statusCode: 400,
        };
        throw error;
      }
      return {
        message: 'Family link confirmed successfully',
        patient_id: mockLinkTokenVerification.patient_id,
        patient: {
          patient_id: mockLinkTokenVerification.patient_id,
          patient_name: mockLinkTokenVerification.patient_name,
          role: 'viewer',
          office_id: mockLinkTokenVerification.office_id,
          office_name: mockLinkTokenVerification.office_name,
        },
      };
    }

    const response = await this.client.post('/api/link/confirm', data);
    return response.data;
  }

  async saveUserProfile(data: { display_name: string }): Promise<{ message: string }> {
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 200));
      return { message: 'プロフィールを作成しました' };
    }

    const response = await this.client.post('/api/user/profile', data);
    return response.data;
  }

  async checkUserProfile(): Promise<{ completed: boolean }> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 200));
      // モックモードではプロフィール完了済みとして扱う
      return { completed: true };
    }

    const response = await this.client.get('/api/user/profile/check');
    return response.data;
  }

  async getLinkedPatients(): Promise<{
    patients: Array<{
      patient_id: number;
      patient_name: string;
      patient_display_name?: string;
      role: string;
      office_id: string;
      office_name?: string;
      relationship?: string;
      service_end_date?: string | null;
      // 本人連携時の読み取り専用表示用（patients テーブル由来、users とは独立）
      birth_date?: string | null;
      gender?: string | null;
    }>;
  }> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 400));
      const scenario = getMockScenario();

      // mock_scenario=self の場合、先頭の患者を本人として扱う。
      // 本人連携は実API同様 birth_date/gender を持つため、読み取り専用表示が
      // 「未登録」にならないよう値を補完する。
      if (scenario === 'self') {
        return {
          patients: mockLinkedPatients.map((p, index) =>
            index === 0
              ? {
                  ...p,
                  relationship: '本人',
                  birth_date: p.birth_date ?? '1950-08-10',
                  gender: p.gender ?? 'male',
                }
              : p
          )
        };
      }

      // mock_scenario=family（および self 以外）の場合は、本人判定にならないよう
      // relationship から '本人' を除外して返す。実API同様、家族連携には
      // birth_date/gender を返さない（null）よう整合させる。
      return {
        patients: mockLinkedPatients.map(p =>
          p.relationship === '本人'
            ? { ...p, relationship: '家族', birth_date: null, gender: null }
            : p
        )
      };
    }

    const response = await this.client.get('/api/user/linked-patients');
    return response.data;
  }

  async getFamiliesByPatient(patientId: number): Promise<{
    families: FamilyMember[];
    patient: {
      patient_id: string;
      name: string;
      office_id: string;
    };
    total_count: number;
  }> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 350));
      const familiesForPatient = mockFamilies.filter(f => f.patient_id === patientId);
      const patient = mockLinkedPatients.find(p => p.patient_id === patientId);
      return {
        families: familiesForPatient,
        patient: patient ? {
          patient_id: String(patient.patient_id),
          name: patient.patient_name,
          office_id: patient.office_id,
        } : {
          patient_id: String(patientId),
          name: '不明',
          office_id: 'office-001',
        },
        total_count: familiesForPatient.length,
      };
    }

    const response = await this.client.get(`/api/patients/${patientId}/families`);
    return response.data;
  }

  async updatePatientDisplayName(
    patientId: number,
    patientDisplayName: string
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      line_user_id: string;
      patient_id: number;
      patient_display_name: string;
      updated_at: string;
    };
  }> {
    const response = await this.client.put(`/api/patients/${patientId}/display-name`, {
      patient_display_name: patientDisplayName,
    });
    return response.data;
  }

  async unlinkFamily(
    patientId: number,
    lineUserId: string
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      line_user_id: string;
      patient_id: number;
    };
  }> {
    const response = await this.client.delete(
      `/api/patients/${patientId}/families/${lineUserId}`
    );
    return response.data;
  }

  /**
   * 連絡帳データを取得
   * @param patientId 患者ID
   * @param date 日付 (YYYY-MM-DD形式)
   * @returns 連絡帳の詳細データ
   */
  async getContactLog(patientId: number, date: string, includePastImages: boolean = false): Promise<ContactLogDetail> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 300));
      const log = mockContactLogs.find(l => l.date === date && l.patient_id === patientId);

      if (!log) {
        const error: ApiError = {
          error: 'NOT_FOUND',
          message: `Contact log not found for date ${date}`,
          statusCode: 404,
        };
        throw error;
      }

      return log as any;
    }

    const response = await this.client.get('/api/contact-logs', {
      params: {
        patient_id: patientId,
        date: date,
        include_past_images: includePastImages,
      },
    });
    return response.data;
  }

  /**
   * 患者の最新連絡帳日付を取得
   * @param patientId 患者ID
   * @returns 最新の連絡帳日付情報
   */
  async getLatestContactLogDate(patientId: number): Promise<{
    patient_id: number;
    latest_date: string | null;
    has_data: boolean;
  }> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 250));
      const latestLog = mockContactLogs.find(l => l.patient_id === patientId);
      return {
        patient_id: patientId,
        latest_date: latestLog ? latestLog.date : null,
        has_data: !!latestLog,
      };
    }

    const response = await this.client.get('/api/contact-logs/latest', {
      params: {
        patient_id: patientId,
      },
    });
    return response.data;
  }

  /**
   * メディアファイルの閲覧用presigned URLを取得
   * @param s3Keys S3キーの配列
   * @returns 閲覧用URLとS3キーのマッピング
   */
  async getMediaViewUrls(s3Keys: string[]): Promise<{
    view_urls: Array<{
      s3_key: string;
      view_url: string;
      error?: string;
    }>;
  }> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 200));
      return {
        view_urls: s3Keys.map((key) => {
          // 動的にview_urlを生成
          const viewUrl = getMockPhotoUrl(key);
          return {
            s3_key: key,
            view_url: viewUrl,
          };
        }),
      };
    }

    const response = await this.client.post('/api/contact-logs/media/view-urls', {
      s3_keys: s3Keys,
    });
    return response.data;
  }

  /**
   * カレンダー用の出席データを取得
   * @param patientId 患者ID
   * @param startDate 開始日 (YYYY-MM-DD形式)
   * @param endDate 終了日 (YYYY-MM-DD形式)
   * @returns 出席データのリスト
   */
  async getAttendanceCalendar(patientId: number, startDate: string, endDate: string): Promise<{
    patient_id: number;
    contact_logs: Array<{
      date: string;
      attendance_id: string;
      attendance_status: 'present' | 'absent' | 'late' | 'early_leave';
      check_in_time?: string;
      check_out_time?: string;
      has_vitals: boolean;
      has_meal: boolean;
      has_hydration: boolean;
      message_preview?: string;
      created_at: string;
    }>;
    total_count: number;
    limit: number;
  }> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 350));
      const filteredLogs = mockAttendanceCalendar.filter(log =>
        log.patient_id === patientId && log.date >= startDate && log.date <= endDate
      );
      return {
        patient_id: patientId,
        contact_logs: filteredLogs,
        total_count: filteredLogs.length,
        limit: 100,
      };
    }

    const response = await this.client.get('/api/contact-logs/list', {
      params: {
        patient_id: patientId,
        start_date: startDate,
        end_date: endDate,
        limit: 100, // カレンダー表示用に多めに取得
      },
    });
    return response.data;
  }

  /**
   * グラフ表示用の健康データを取得
   * @param patientId 患者ID
   * @param startDate 開始日 (YYYY-MM-DD形式)
   * @param endDate 終了日 (YYYY-MM-DD形式)
   * @param types データタイプ（オプション）temperature, blood_pressure, meals, hydration
   * @returns 健康データと統計サマリー
   */
  async getHealthData(
    patientId: number,
    startDate: string,
    endDate: string,
    types?: string[]
  ): Promise<HealthDataResponse> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 450));
      const filteredData = mockHealthDataExtended.filter(d =>
        d.date >= startDate && d.date <= endDate
      );

      // 統計サマリーを計算
      const temps = filteredData.map(d => d.vitals?.temp_c).filter(Boolean) as number[];
      const sbps = filteredData.map(d => d.vitals?.sbp).filter(Boolean) as number[];
      const dbps = filteredData.map(d => d.vitals?.dbp).filter(Boolean) as number[];
      const pulses = filteredData.map(d => d.vitals?.pulse).filter(Boolean) as number[];
      const spo2s = filteredData.map(d => d.vitals?.spo2).filter(Boolean) as number[];
      const meals = filteredData.map(d => d.meals).filter(Boolean).filter(meal => !meal.is_not_provided);
      const hydrations = filteredData.map(d => d.hydration_ml).filter(Boolean) as number[];

      return {
        patient_id: patientId,
        period: { start_date: startDate, end_date: endDate },
        health_data: filteredData,
        summary: {
          days_with_data: filteredData.length,
          temperature: temps.length > 0 ? {
            avg: temps.reduce((a, b) => a + b, 0) / temps.length,
            max: Math.max(...temps),
            min: Math.min(...temps),
          } : undefined,
          blood_pressure_sys: sbps.length > 0 ? {
            avg: sbps.reduce((a, b) => a + b, 0) / sbps.length,
            max: Math.max(...sbps),
            min: Math.min(...sbps),
          } : undefined,
          blood_pressure_dia: dbps.length > 0 ? {
            avg: dbps.reduce((a, b) => a + b, 0) / dbps.length,
            max: Math.max(...dbps),
            min: Math.min(...dbps),
          } : undefined,
          pulse: pulses.length > 0 ? {
            avg: pulses.reduce((a, b) => a + b, 0) / pulses.length,
            max: Math.max(...pulses),
            min: Math.min(...pulses),
          } : undefined,
          spo2: spo2s.length > 0 ? {
            avg: spo2s.reduce((a, b) => a + b, 0) / spo2s.length,
            max: Math.max(...spo2s),
            min: Math.min(...spo2s),
          } : undefined,
          meals: meals.length > 0 ? {
            staple_intake: {
              avg: meals.reduce((a, b) => a + b.staple_intake, 0) / meals.length,
            },
            side_intake: {
              avg: meals.reduce((a, b) => a + b.side_intake, 0) / meals.length,
            },
            soup_intake: {
              avg: meals.reduce((a, b) => a + b.soup_intake, 0) / meals.length,
            }
          } : undefined,
          hydration: hydrations.length > 0 ? {
            total: hydrations.reduce((a, b) => a + b, 0),
            avg: hydrations.reduce((a, b) => a + b, 0) / hydrations.length,
            max: Math.max(...hydrations),
          } : undefined,
        },
      };
    }

    const params: any = {
      patient_id: patientId,
      start_date: startDate,
      end_date: endDate,
    };

    if (types && types.length > 0) {
      params.types = types.join(',');
    }

    const response = await this.client.get('/api/health-data', {
      params,
    });
    return response.data;
  }

  // 旧 /api/consent 系（第三者提供同意 / 患者単位）の apiClient メソッドは PR-3 で
  // /consent 画面が通信の秘密同意に切り替わったことにより呼び出し元がゼロになったため、
  // PR-4 で以下 3 メソッドを削除:
  //   - submitConsent (POST /api/consent)
  //   - getCurrentConsent (GET /api/consent/current/:patientId)
  //   - getConsentHistory (GET /api/consent/history/:patientId)
  // BE 側の `services/api/handlers/consent.py`（上記 3 EP）も呼び出し元が無いため
  // 2026-10-15 に削除済み。旧 `consents` テーブル自体は監査保持のため残る。

  // ==========================================================================
  // 通信の秘密同意 API（電気通信事業法 第4条 / LINE アカウント単位・Web 取得）
  // 詳細: docs/pre-design/communication-secret-consent/design.md
  // ==========================================================================

  /**
   * 通信の秘密同意の現行説明文・version・hash を取得する。PP / 利用規約 URL は
   * consent_document_text にリンクとして埋め込まれている。
   * クライアントは本レスポンスをそのまま表示し、POST 時に hash を同じ値で送付する。
   */
  async getCommunicationSecretConsentDocument(): Promise<{
    version_major: number;
    version_minor: number;
    consent_document_text: string;
    consent_document_hash: string;
  }> {
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 200));
      return {
        version_major: 1,
        version_minor: 0,
        // family_app/consent_documents.py の CONSENT_DOCUMENT_TEXT を手動コピー。
        // 本番文面が更新されたら本 mock も追従させること（SSOT は Python 側）。
        consent_document_text: `いつもご利用いただきありがとうございます。 お客様に便利で安心して本サービスをお使いいただくため、株式会社Rehab for JAPANによる以下の情報の取り扱いにご同意をお願いいたします。

**① より良いサービスにするために**

お客様の「欠席・振替の連絡」や「本サービスの利用日時」などのデータは、本サービスの改善や便利な新機能の開発のために活用させていただきます。

**② 安全にサポートするために**

お客様がやり取りする「メッセージや写真の内容」を、私たちが勝手に見ることはありません。 ただし、お客様からのお問い合わせ対応や、本サービスの不具合の調査など、必要なサポートや保守を行う場合に限り、例外的に確認させていただくことがあります。

※詳しい情報の取り扱いについては、[利用規約（第9条）](https://cdn.rehab.cloud/familyapp/terms_of_service.pdf)および[プライバシーポリシー](https://cdn.rehab.cloud/familyapp/privacy_policy.pdf)をご確認ください。
`,
        consent_document_hash: 'a'.repeat(64),
      };
    }

    const response = await this.client.get('/api/consent/communication-secret/document');
    return response.data;
  }

  /**
   * 呼び出し LINE アカウントの通信の秘密同意 取得状況を取得する。
   * `has_current === false` なら未取得（バージョン不一致含む）で `/consent` へ誘導する。
   */
  async getCommunicationSecretConsentStatus(): Promise<{
    has_current: boolean;
    current_version: { major: number; minor: number };
    consented_version: { major: number; minor: number } | null;
    consent_date: string | null;
  }> {
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 150));
      // mock_scenario=no_communication_consent なら未取得を返す
      const scenario = getMockScenario();
      if (scenario === 'no_communication_consent') {
        return {
          has_current: false,
          current_version: { major: 1, minor: 0 },
          consented_version: null,
          consent_date: null,
        };
      }
      return {
        has_current: true,
        current_version: { major: 1, minor: 0 },
        consented_version: { major: 1, minor: 0 },
        consent_date: '2026-07-01T10:30:00.000Z',
      };
    }

    const response = await this.client.get('/api/consent/communication-secret/status');
    return response.data;
  }

  /**
   * 通信の秘密同意を保存する。
   * BE 側で `consent_document_hash` を再算出して照合し、不一致は 400 CONSENT_DOCUMENT_MISMATCH。
   */
  async submitCommunicationSecretConsent(data: {
    version_major: number;
    version_minor: number;
    consent_document_hash: string;
  }): Promise<{
    success: boolean;
    line_user_id: string;
    consent_date: string;
    version_major: number;
    version_minor: number;
  }> {
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true,
        line_user_id: 'mock-user-123',
        consent_date: new Date().toISOString(),
        version_major: data.version_major,
        version_minor: data.version_minor,
      };
    }

    const response = await this.client.post('/api/consent/communication-secret', data);
    return response.data;
  }

  /**
   * コンテンツを既読としてマーク
   */
  async markContentAsViewed(data: {
    content_id: string;
    patient_id: number;
    date: string;
    view_duration_seconds: number;
  }): Promise<{
    content_id: string;
    viewed_at: string;
    is_new_view: boolean;
  }> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        content_id: data.content_id,
        viewed_at: new Date().toISOString(),
        is_new_view: true,
      };
    }

    const response = await this.client.post('/api/content-views', data);
    return response.data;
  }

  /**
   * メッセージスレッドを取得
   */
  async getMessagesThread(
    patientId: number,
    options?: { limit?: number; before_message_id?: number }
  ): Promise<MessageThread> {
    // 開発モードの場合はモックデータを返す
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 400));
      // 患者IDに対応するモックデータを取得（なければデフォルト）
      const threadData = mockMessagesThreads[patientId] || mockMessagesThread;
      return {
        ...threadData,
        patient_id: patientId,
      };
    }

    const params: any = { patient_id: patientId };
    if (options?.limit) params.limit = options.limit;
    if (options?.before_message_id) params.before_message_id = options.before_message_id;

    const response = await this.client.get('/api/messages/threads', { params });
    return response.data;
  }

  /**
   * 音声データをテキストに変換（Speech-to-Text）
   */
  async speechToText(
    audioBase64: string,
    contentType: string,
  ): Promise<{ transcript: string }> {
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { transcript: '開発モードの音声認識テストです。' };
    }

    const response = await this.client.post('/api/speech-to-text', {
      audio_base64: audioBase64,
      content_type: contentType,
    });
    return response.data;
  }

  /**
   * メッセージの確認済みステータスを取得（軽量ポーリング用）
   */
  async getMessageConfirmedStatus(patientId: number): Promise<MessageConfirmedStatusResponse> {
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 100));
      // モックデータ: mockMessagesThreadsから確認ステータスを生成
      const threadData = mockMessagesThreads[patientId] || mockMessagesThread;
      const statuses: MessageConfirmedStatusItem[] = [];
      for (const thread of threadData.threads) {
        for (const msg of thread.messages) {
          if (msg.direction === 'outgoing') {
            statuses.push({
              thread_id: thread.thread_id,
              message_id: msg.message_id,
              confirmed_at: msg.confirmed_at || null,
            });
          }
        }
      }
      return { statuses };
    }

    const response = await this.client.get('/api/messages/confirmed-status', {
      params: { patient_id: patientId },
    });
    return response.data;
  }

  /**
   * メッセージを送信
   */
  async sendMessage(data: SendMessageRequest): Promise<SendMessageResponse> {
    // 開発モードの場合はモック送信
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 500));
      const now = new Date().toISOString();
      return {
        success: true,
        message_id: Date.now(),
        patient_id: data.patient_id,
        created_at: now,
        sender_name: '田中太郎',
      };
    }

    const response = await this.client.post('/api/messages', data);
    return response.data;
  }

  /**
   * AI相談 - 会話を開始
   */
  async createAiChatConversation(data: {
    topic: 'daily' | 'health' | 'memory';
    conversation_starters?: string[];
  }): Promise<AiChatCreateResponse> {
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      const now = new Date().toISOString();
      return {
        conversation_id: `mock_user_${Date.now()}_${data.topic}`,
        topic: data.topic,
        message: {
          message_id: 'msg_001_assistant',
          role: 'assistant',
          content: 'おはようございます。本日もデイサービスにお越しいただきありがとうございます。今日のお話をさせていただきますね。',
          quick_replies: ['今日はどんな活動をしましたか', '体調はいかがですか', '最近のお天気の話'],
          created_at: now,
        },
      };
    }

    const response = await this.client.post('/api/ai-chat/conversations', data);
    return response.data;
  }

  /**
   * AI相談 - メッセージ送信
   */
  async sendAiChatMessage(conversationId: string, content: string): Promise<AiChatMessageResponse> {
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const now = new Date().toISOString();
      return {
        conversation_id: conversationId,
        message: {
          message_id: `msg_${Date.now()}_assistant`,
          role: 'assistant',
          content: 'お話しくださりありがとうございます。それはとても良いことですね。季節の移ろいを感じながら、穏やかにお過ごしいただけているようで何よりでございます。',
          quick_replies: ['もう少し詳しく教えてください', '他にお話ししたいことはありますか', '今日はこのあたりにしましょう'],
          created_at: now,
        },
      };
    }

    const response = await this.client.post(
      `/api/ai-chat/conversations/${conversationId}/messages`,
      { content }
    );
    return response.data;
  }

  /**
   * AI相談 - 会話履歴を取得
   */
  async getAiChatConversation(conversationId: string): Promise<AiChatConversation> {
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        conversation_id: conversationId,
        topic: 'daily',
        messages: [],
      };
    }

    const response = await this.client.get(`/api/ai-chat/conversations/${conversationId}`);
    return response.data;
  }

  /**
   * AI相談 - 会話要約を生成
   */
  async generateAiChatSummary(conversationId: string): Promise<AiChatSummaryResponse> {
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 2000));
      return {
        conversation_id: conversationId,
        summary: '本日はデイサービスでの活動について穏やかにお話しされました。お食事を美味しく召し上がったご様子で、午後のレクリエーションも楽しまれたとのことです。',
        mood: 'positive',
        topics: ['デイサービスの活動', '食事', 'レクリエーション'],
      };
    }

    const response = await this.client.post(`/api/ai-chat/conversations/${conversationId}/summary`);
    return response.data;
  }

  /**
   * 歳時記 - 本日のエントリーを取得
   */
  async getSaijikiToday(): Promise<SaijikiEntry> {
    if (isDevelopmentMode()) {
      await new Promise(resolve => setTimeout(resolve, 300));
      const month = new Date().getMonth() + 1;
      const day = new Date().getDate();
      return {
        date_key: `${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`,
        title: '春分',
        description: '昼と夜の長さがほぼ等しくなる日。自然界が目覚める季節の到来です。',
        detail: '古来より、春分は五穀豊穣を祈り、祖先を敬う日とされてきました。',
        season: 'spring',
        category: 'sekki',
        conversation_starters: [
          'お彼岸のお墓参りには行かれましたか',
          '春のお花は何がお好きですか',
          'この時期の思い出をお聞かせください',
        ],
      };
    }

    const response = await this.client.get('/api/saijiki/today');
    return response.data;
  }

}

export default new ApiClient();